<template>
  <BittsPopover :show-menu="showCreateListMenu" placement="bottomRight">
    <template #default>
      <BittsButton
        :left-icon="['fak', 'add']"
        text="New"
        @click="showCreateListMenu = !showCreateListMenu"
      />
    </template>
    <template #content>
      <div class="max-w-[300px]" ref="createListMenuRef">
        <div class="create_new_list_section-header">Build Your Own</div>
        <div class="px-4">
          <button
            v-for="option in bulidYourOwnOptions"
            :key="option.value"
            type="button"
            :disabled="isFreeTier && option.requiresUpgrade"
            :data-testid="`create_new_list_option_${option.value}`"
            :class="[
              'create_new_list_option',
              {
                'create_new_list_option--disabled':
                  isFreeTier && option.requiresUpgrade,
              },
            ]"
            @click="createListType(option)"
          >
            <FontAwesomeIcon
              :icon="option.icon"
              class="text-neutral-accent h-16 w-16"
            />
            <div class="flex flex-col items-start flex-1">
              <div class="flex items-center justify-between gap-8 w-full">
                <p>{{ option.text }}</p>
                <BillingCTA
                  v-if="isFreeTier && option.requiresUpgrade"
                  size="x-small"
                  class="create_new_list_upgrade-cta"
                  :billing-interaction="{
                    cta: LISTS_CTAS.CREATE_LIST,
                    event_site: EVENT_SITES.LISTS_PAGE_CREATE_DROPDOWN,
                  }"
                  @click="() => (showCreateListMenu = false)"
                />
              </div>
              <p class="text-neutral-text text-sm">
                {{ option.description }}
              </p>
            </div>
          </button>
        </div>
        <BittsDivider class="m-0 mt-4" />
        <div class="create_new_list_section-header">Quick Start</div>
        <div class="px-4">
          <button
            v-for="option in QUICK_START_OPTIONS"
            :key="option.value"
            type="button"
            :data-testid="`create_new_list_option_${option.value}`"
            class="create_new_list_option"
            @click="createListType(option)"
          >
            <FontAwesomeIcon
              :icon="option.icon"
              class="text-neutral-accent h-16 w-16"
            />
            <div class="flex flex-col items-start">
              <p>{{ option.text }}</p>
              <p class="text-neutral-text text-sm">
                {{ option.description }}
              </p>
            </div>
          </button>
        </div>

        <BittsDivider class="m-0 my-4" />

        <!-- Folder option -->
        <div class="px-4 mb-4">
          <button
            type="button"
            :disabled="isFreeTier"
            data-testid="create_new_list_option_folder"
            :class="[
              'create_new_list_option',
              { 'create_new_list_option--disabled': isFreeTier },
            ]"
            @click="() => onShowCreateFolderModal()"
          >
            <FontAwesomeIcon
              :icon="['fak', 'folder']"
              class="text-neutral-accent h-16 w-16"
            />
            <div class="flex flex-col items-start flex-1">
              <p>Folder</p>
            </div>
            <BillingCTA
              v-if="isFreeTier"
              size="x-small"
              class="create_new_list_upgrade-cta"
              :billing-interaction="{
                cta: LISTS_CTAS.CREATE_LIST,
                event_site: EVENT_SITES.LISTS_PAGE_CREATE_DROPDOWN,
              }"
            />
          </button>
        </div>
      </div>
    </template>
  </BittsPopover>
</template>

<script setup lang="ts">
import { BittsButton, BittsDivider, BittsPopover } from '@crossbeam/bitts';
import { EVENT_SITES, LISTS_CTAS } from '@crossbeam/itly';

import { onClickOutside } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { RouteLocationRaw, useRoute, useRouter } from 'vue-router';

import BillingCTA from '@/components/billing/BillingCTA.vue';

import { useBillingStore } from '@/stores';

const emit = defineEmits<(e: 'show-create-folder', lists: string[]) => void>();
const router = useRouter();
const { isFreeTier } = storeToRefs(useBillingStore());

type ListOption =
  | (typeof bulidYourOwnOptions.value)[number]
  | (typeof QUICK_START_OPTIONS)[number];

const route = useRoute();
const from = computed(() => route.name?.toString());

const bulidYourOwnOptions = computed(() => [
  {
    text: 'Single Partner',
    value: 'single-partner-overlaps',
    icon: ['fak', 'single-partner'],
    description: 'Account map with a single partner',
    createRoute: `${from.value}.create`,
    requiresUpgrade: false,
  },
  {
    text: 'Greenfield',
    value: 'single-partner-greenfield',
    icon: ['fak', 'greenfield'],
    description: 'See all of your non-overlapping accounts with partners',
    createRoute: `${from.value}.create`,
    requiresUpgrade: true,
  },
  {
    text: 'Custom',
    value: 'custom',
    icon: ['fak', 'wrench'],
    description: 'Build a List using your own filters',
    createRoute: `${from.value}.create`,
    requiresUpgrade: true,
  },
]);

const showCreateListMenu = ref(false);
const createListMenuRef = ref<HTMLElement | null>(null);
onClickOutside(createListMenuRef, () => (showCreateListMenu.value = false));

const QUICK_START_OPTIONS = [
  {
    text: 'Account Mapping',
    value: 'account-mapping',
    icon: ['fak', 'overlap'],
    description: 'A complete view of account overlap across all partners',
    createRoute: 'home_account_mapping',
    requiresUpgrade: false,
  },
  {
    text: 'Pipeline Generation',
    value: 'pipeline-generation',
    icon: ['fak', 'generate-new-pipeline'],
    description:
      "Discover high-potential prospects in your partners' customers",
    createRoute: 'generate-pipeline',
    requiresUpgrade: false,
  },
  {
    text: 'Deal Navigator',
    value: 'deal-navigator',
    icon: ['fak', 'opportunity'],
    description:
      "View open opportunities that overlap with your partners' customers",
    createRoute: 'deal_navigator',
    requiresUpgrade: false,
  },
];

function onShowCreateFolderModal(lists: string[] = []) {
  emit('show-create-folder', lists);
  showCreateListMenu.value = false;
}

function createListType(
  // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
  option: ListOption = bulidYourOwnOptions.value[0]!,
) {
  if (isFreeTier.value && option.requiresUpgrade) return;

  const routeObj: RouteLocationRaw = { name: option.createRoute };
  if (option.createRoute === `${from.value}.create`) {
    routeObj.query = { type: option.value };
  }
  router.push(routeObj);
  showCreateListMenu.value = false;
}

defineExpose({ createListType });
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.create_new_list_section-header {
  @apply px-8 pt-4 text-neutral-text-weak text-sm;
}

.create_new_list_option {
  @apply flex items-center justify-start gap-8 py-4 px-8 cursor-pointer w-full rounded-8 hover:bg-neutral-background-weak text-left;
}

.create_new_list_option--disabled {
  @apply cursor-not-allowed hover:bg-transparent;
}

.create_new_list_upgrade-cta :deep(.bitts-tag .ant-tag) {
  @apply rounded-bts-lg leading-4 cursor-pointer;
}
</style>
